import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usedListingService, bodyListingService, resolveProductMediaUrl } from '../../../services/apiService';

function formatCountdown(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function UsedPartOrderStatus({ order }) {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [approving, setApproving] = useState(false);
  const bodyMeta = order?.bodyPartMeta;
  const meta = order?.usedPartMeta;
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    if (meta?.flowType !== 'express_buy' || !meta?.sellerConfirmDeadline) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [meta]);

  const handleApproveBodyPhoto = async () => {
    setApproving(true);
    try {
      const res = await bodyListingService.approveDispatchPhoto({
        orderId: order.id,
        listingId: bodyMeta?.listingId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const checkoutMeta = res.bodyPartMeta || res.data?.bodyPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({ ...item, bodyPartMeta: checkoutMeta })
      );
      toast.success(
        bodyMeta?.dispatchPhotoApproved
          ? 'Continue to checkout.'
          : 'Photo approved — proceed to checkout.'
      );
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Approval failed.');
    } finally {
      setApproving(false);
    }
  };

  if (bodyMeta?.flowType === 'check_availability') {
    const photoUrl = bodyMeta.dispatchPhotoUrl || meta?.verificationPhotoUrl;
    const awaiting = bodyMeta.status === 'awaiting_seller_photo' && !photoUrl;
    const photoReady = Boolean(photoUrl);
    const paymentPending =
      order.paymentStatus === 'Pending' || order.status === 'Pending Payment';
    const showCheckoutCta = photoReady && paymentPending;

    return (
      <div className="mt-3 space-y-3">
        <div className="rounded-lg border border-blue-700/40 bg-blue-950/30 px-3 py-2 text-xs text-blue-200">
          {awaiting && (
            <>
              <p className="font-semibold">Request sent — awaiting seller photo.</p>
              <p className="mt-1 text-blue-300/80">
                The seller will check this panel and send you a photo before you pay.
              </p>
            </>
          )}
          {photoReady && bodyMeta.dispatchPhotoApproved && !paymentPending && (
            <p className="font-semibold text-emerald-300">Pre-dispatch photo approved.</p>
          )}
          {photoReady && (!bodyMeta.dispatchPhotoApproved || paymentPending) && (
            <p className="font-semibold text-emerald-300">
              Seller sent a verification photo — review below.
            </p>
          )}
        </div>

        {photoReady && (
          <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3">
            <img
              src={resolveProductMediaUrl(photoUrl)}
              alt="Seller verification"
              className="max-h-48 w-full rounded-lg object-contain bg-slate-900"
            />
            {showCheckoutCta && (
              <button
                type="button"
                onClick={handleApproveBodyPhoto}
                disabled={approving}
                className="mt-3 w-full rounded-lg bg-emerald-600 py-2.5 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {approving
                  ? 'Please wait…'
                  : bodyMeta.dispatchPhotoApproved
                    ? 'Continue to checkout'
                    : 'Approve photo — checkout'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!meta?.flowType) return null;

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const res = await usedListingService.proceedAfterVerificationPhoto({
        orderId: order.id,
        listingId: meta.listingId,
      });
      const item = res.checkoutItem || res.data?.checkoutItem;
      const usedMeta = res.usedPartMeta || res.data?.usedPartMeta;
      if (!item) throw new Error('Checkout session unavailable');

      localStorage.removeItem('buyNowItem');
      localStorage.setItem(
        'buyNowItem',
        JSON.stringify({ ...item, usedPartMeta: usedMeta })
      );
      toast.success('Proceed to checkout.');
      navigate('/checkout/address');
    } catch (err) {
      toast.error(err.message || 'Could not proceed to checkout.');
    } finally {
      setPaying(false);
    }
  };

  if (meta.flowType === 'check_availability') {
    const photoUrl = meta.verificationPhotoUrl;
    const awaiting = meta.status === 'awaiting_seller_photo';
    const photoReady = meta.status === 'photo_received' && photoUrl;

    return (
      <div className="mt-3 space-y-3">
        <div className="rounded-lg border border-blue-700/40 bg-blue-950/30 px-3 py-2 text-xs text-blue-200">
          {awaiting && (
            <>
              <p className="font-semibold">Request sent — awaiting seller response.</p>
              <p className="mt-1 text-blue-300/80">
                The seller will physically check this part and send you a photo within 4 hours.
              </p>
            </>
          )}
          {photoReady && (
            <p className="font-semibold text-emerald-300">Seller sent a verification photo — review below.</p>
          )}
          {meta.gradeLabel && (
            <span className="mt-1 block text-blue-300/80">{meta.gradeLabel}</span>
          )}
        </div>

        {photoReady && (
          <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3">
            <img
              src={resolveProductMediaUrl(photoUrl)}
              alt="Seller verification"
              className="max-h-48 w-full rounded-lg object-contain bg-slate-900"
            />
            <button
              type="button"
              onClick={handlePayNow}
              disabled={paying}
              className="mt-3 w-full rounded-lg bg-amber-500 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
            >
              {paying ? 'Please wait…' : 'Pay now'}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (meta.flowType === 'express_buy') {
    const deadline = meta.sellerConfirmDeadline
      ? new Date(meta.sellerConfirmDeadline).getTime()
      : null;
    const remaining = deadline ? deadline - now : null;

    return (
      <div className="mt-3 rounded-lg border border-amber-700/40 bg-amber-950/30 px-3 py-2 text-xs text-amber-200">
        <p className="font-semibold">Express Buy — pending seller confirmation</p>
        {remaining != null && remaining > 0 ? (
          <p className="mt-1 text-amber-300">
            Auto-refund in <strong>{formatCountdown(remaining)}</strong> if seller does not confirm
          </p>
        ) : remaining != null ? (
          <p className="mt-1 text-amber-300">Confirmation window elapsed — refund processing</p>
        ) : null}
      </div>
    );
  }

  return null;
}
